<script setup lang="ts">
import { computed } from 'vue'

import { useReportCellFormatter } from '../../../composables/useReportCellFormatter'
import type { ReportColumn } from '../../../models/column.types'
import type { ReportRow } from '../../../models/report.types'

const props = defineProps<{
  row: ReportRow
  column: ReportColumn
}>()

const { formatCell } = useReportCellFormatter()

type StatusTone = 'neutral' | 'success' | 'warning' | 'danger'

const WARNING = new Set([
  'pending',
  'paused',
  'adset_paused',
  'not_eligible',
  'campaign_paused',
  'coming soon',
  'warning',
  'planned',
  'completed',
  'non-branded',
  'active (limited)',
])
const DANGER = new Set([
  'disable',
  'disabled',
  'removed',
  'deleted',
  'archived',
  'disapproved',
  'false',
  'action',
  'at risk',
])
const SUCCESS = new Set([
  'active',
  'ad_active',
  'adset_active',
  'campaign_active',
  'true',
  'available',
  'enable',
  'enabled',
  'eligible',
  'information',
  'branded',
  'healthy',
])

const displayValue = computed(() => formatCell(props.row[props.column.field], props.column))
const tone = computed<StatusTone>(() => {
  const key = displayValue.value.trim().toLowerCase()
  if (SUCCESS.has(key)) return 'success'
  if (WARNING.has(key)) return 'warning'
  if (DANGER.has(key)) return 'danger'
  return 'neutral'
})
</script>

<template>
  <span
    class="report-status-cell mx-auto inline-flex min-w-0 max-w-full justify-center truncate rounded-sm border px-2 py-0.5 text-2xs font-bold"
    :class="[
      `report-status-cell--${tone}`,
      tone === 'success'
        ? 'border-status-active-fg bg-status-active-bg text-status-active-fg'
        : tone === 'warning'
          ? 'border-status-paused-fg bg-status-paused-bg text-status-paused-fg'
          : tone === 'danger'
            ? 'border-status-removed-fg bg-status-removed-bg text-status-removed-fg'
            : 'border-status-neutral-fg bg-status-neutral-bg text-status-neutral-fg',
    ]"
    data-testid="report-status-cell"
  >
    {{ displayValue }}
  </span>
</template>
